import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import apiClient from "../services/api";
import { useHotel } from "../contexts/HotelContext";
import EditCompetitionModal from "../components/grants/competitions/EditCompetitionModal";
import MultiSelectDropdown, { Option } from "../components/common/MultiSelectDropdown";
import { EmptyState } from "../components/common/EmptyState";
import { competitionStatusLabels, competitionStatusColors } from "../constants/grants-labels";

interface Competition {
  id: string;
  name: string;
  code: string | null;
  actionCode: string | null;
  description: string | null;
  targetGroup: string | null;
  status: string;
  applicationStartDate: string | null;
  applicationEndDate: string | null;
  officialUrl: string | null;
  isActive: boolean;
  program: {
    id: string;
    code: string;
    name: string;
    shortName: string | null;
  } | null;
  callsCount: number;
  createdAt: string;
  updatedAt: string;
}

interface CompetitionsResponse {
  competitions: Competition[];
  total: number;
}

/**
 * Page for managing grant competitions (PARP contests).
 * Competitions group operator projects under a program.
 */
const GrantsCompetitions: React.FC = () => {
  const { user, hasModuleEnabled } = useHotel();
  const canWrite = user?.role === "admin" || hasModuleEnabled("admin:kiedynabory:write");
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingCompetitionId, setEditingCompetitionId] = useState<string | null>(
    null
  );
  const [programFilters, setProgramFilters] = useState<string[]>([]);
  const [statusFilters, setStatusFilters] = useState<string[]>([]);

  const handleRowClick = (competitionId: string) => {
    navigate(`/grants/competitions/${competitionId}`);
  };

  const handleRowKeyDown = (e: React.KeyboardEvent, competitionId: string) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      navigate(`/grants/competitions/${competitionId}`);
    }
  };

  const { data, isLoading, error, refetch } = useQuery<CompetitionsResponse>({
    queryKey: ["grants-competitions", programFilters, statusFilters],
    queryFn: async () => {
      const params = new URLSearchParams();
      programFilters.forEach(id => params.append("programId", id));
      statusFilters.forEach(status => params.append("status", status));
      const { data } = await apiClient.get(
        `/grants/competitions?${params.toString()}`
      );
      return data;
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/grants/competitions/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["grants-competitions"] });
    },
  });

  const handleDelete = (competition: Competition) => {
    if (competition.callsCount > 0) {
      alert(
        `Nie można usunąć konkursu "${competition.name}" - ma powiązane nabory.`
      );
      return;
    }
    if (
      window.confirm(
        `Czy na pewno chcesz usunąć konkurs "${competition.name}"?`
      )
    ) {
      deleteMutation.mutate(competition.id);
    }
  };

  // Extract unique programs for filter
  const programOptions: Option[] = React.useMemo(() => {
    if (!data?.competitions) return [];
    const programMap = new Map<string, { code: string; name: string }>();
    data.competitions.forEach((competition) => {
      if (competition.program) {
        programMap.set(competition.program.id, {
          code: competition.program.code,
          name: competition.program.shortName || competition.program.name,
        });
      }
    });
    return Array.from(programMap.entries()).map(([id, prog]) => ({
      value: id,
      label: `${prog.code} - ${prog.name}`,
    }));
  }, [data?.competitions]);

  // Extract unique statuses for filter
  const statusOptions: Option[] = React.useMemo(() => {
    return Object.entries(competitionStatusLabels).map(([value, label]) => ({
      value,
      label,
    }));
  }, []);

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-neutral-900 text-left">Konkursy</h1>
            <p className="mt-1 text-sm text-neutral-600 text-left">
              Lista konkursów w ramach programów
            </p>
          </div>
          {canWrite && (
            <button
              onClick={() => setIsAddModalOpen(true)}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700"
            >
              Dodaj konkurs
            </button>
          )}
        </div>

        {/* Filters */}
        <div className="flex items-center gap-4">
          {programOptions.length > 0 && (
            <div className="w-64">
              <label className="text-sm text-neutral-600 mb-1 block">Program:</label>
              <MultiSelectDropdown
                options={programOptions}
                selectedValues={programFilters}
                onChange={setProgramFilters}
                placeholder="Wszystkie programy"
              />
            </div>
          )}
          <div className="w-64">
            <label className="text-sm text-neutral-600 mb-1 block">Status:</label>
            <MultiSelectDropdown
              options={statusOptions}
              selectedValues={statusFilters}
              onChange={setStatusFilters}
              placeholder="Wszystkie statusy"
            />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-neutral-200">
          {isLoading ? (
            <div className="p-6 text-center" role="status" aria-live="polite">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-2 text-sm text-neutral-600">
                Ładowanie konkursów...
              </p>
            </div>
          ) : error ? (
            <div className="p-6 text-center">
              <p className="text-red-600">Błąd podczas pobierania danych</p>
              <button
                onClick={() => refetch()}
                className="mt-2 text-primary-600 hover:text-primary-800"
              >
                Spróbuj ponownie
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-neutral-200">
                <thead className="bg-neutral-50">
                  <tr>
                    <th className="px-4 py-3 text-center text-xs font-medium text-neutral-500 uppercase">
                      Program
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase">
                      Nazwa projektu
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase">
                      Program
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-neutral-500 uppercase">
                      Status
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-neutral-500 uppercase">
                      Naborów
                    </th>
                    {canWrite && (
                      <th className="px-4 py-3 text-right text-xs font-medium text-neutral-500 uppercase">
                        Akcje
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-neutral-200">
                  {data?.competitions?.map((competition) => (
                    <tr
                      key={competition.id}
                      className="hover:bg-neutral-50 cursor-pointer focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500 focus-visible:outline-none"
                      onClick={() => handleRowClick(competition.id)}
                      onKeyDown={(e) => handleRowKeyDown(e, competition.id)}
                      tabIndex={0}
                      role="row"
                      aria-label={`Otwórz szczegóły konkursu ${competition.name}`}
                    >
                      <td className="px-4 py-4 whitespace-nowrap text-center">
                        {competition.program && (
                          <Link
                            to={`/grants/programs/${competition.program.id}`}
                            className="inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-800 hover:bg-primary-200"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {competition.program.code}
                          </Link>
                        )}
                      </td>
                      <td className="px-4 py-4 text-left">
                        <div className="text-sm font-medium text-neutral-900">
                          {competition.name}
                        </div>
                        {competition.code && (
                          <div className="text-xs text-neutral-500">
                            {competition.code}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-neutral-500">
                        {competition.actionCode || "-"}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-center">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                            competitionStatusColors[competition.status] ||
                            "bg-neutral-100 text-neutral-800"
                          }`}
                        >
                          {competitionStatusLabels[competition.status] ||
                            competition.status}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-right">
                        {competition.callsCount > 0 ? (
                          <Link
                            to={`/grants/calls?competitionId=${competition.id}`}
                            className="text-sm text-primary-600 hover:text-primary-800"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {competition.callsCount}
                          </Link>
                        ) : (
                          <span className="text-sm text-neutral-500">0</span>
                        )}
                      </td>
                      {canWrite && (
                        <td className="px-4 py-4 whitespace-nowrap text-right cursor-default">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingCompetitionId(competition.id);
                            }}
                            className="text-primary-600 hover:text-primary-800 mr-3"
                            aria-label={`Edytuj konkurs ${competition.name}`}
                          >
                            Edytuj
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(competition);
                            }}
                            className="text-red-600 hover:text-red-800"
                            disabled={competition.callsCount > 0}
                            aria-label={`Usuń konkurs ${competition.name}`}
                          >
                            Usuń
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>

              {(!data?.competitions || data.competitions.length === 0) && (
                <EmptyState
                  title="Brak konkursów do wyświetlenia"
                  className="p-6"
                />
              )}
            </div>
          )}
        </div>

        {/* Add/Edit Modal */}
        <EditCompetitionModal
          isOpen={isAddModalOpen || !!editingCompetitionId}
          onClose={() => {
            setIsAddModalOpen(false);
            setEditingCompetitionId(null);
          }}
          competitionId={editingCompetitionId}
        />
      </div>
    </Layout>
  );
};

export default GrantsCompetitions;
